import React, {useState} from 'react';
import {EditorState, convertToRaw, ContentState} from 'draft-js';
import {Editor} from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import './DraftEditor.css';
import CustomColorPicker from './CustomColorPicker';
import colorUtils from '../../utils/colorUtils';
import _remove from 'lodash/remove';
/**
 * @param toolbarOnFocus - Toolbar is visible only when editor is focused.
 * @param type - Accepted values are input and textarea.
 * @param field
 * @param name
 * @param value
 * @param form
 * @param containerClassname
 * @param containerStyle
 * @param editorClassname
 * @param disabled - Set editor to read-only.
 * @param enableLink - Enable link function in editor toolbar.
 * @param enableTextAlign - Enable text align function in editor toolbar.
 * @param editorProps - Object. Additional props for <Editor />
 * @param fontList - Customize font list
 * @param setTextLength - Store editor plain text length in parent component to validate max input length
 * @returns {*}
 * @constructor
 */
export default function DraftEditor({
  toolbarOnFocus = false,
  type = 'input',
  field = {},
  name,
  value,
  form,
  containerClassname,
  containerStyle,
  editorClassname = '',
  disabled = false,
  enableLink,
  enableTextAlign,
  editorProps,
  fontList,
  setTextLength,
  placeholder = '',
  disableFontSize = false,
  disableFontFamily = false,
  disableToolbar = false,
  maxLength = 50,
}) {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [init, setInit] = useState(true);
  const [error, setError] = useState(false);
  const settings = {
    editorClassName: `form-control DraftEditor-basic-editor ${editorClassname} ${
      colorUtils.hasWhite(field.value) ? 'colored-bg-for-white-text' : ''
    } ${disabled ? 'disabled' : ''}`,
    wrapperClassName: 'DraftEditor-basic-wrapper',
    toolbarClassName:
      'DraftEditor-basic-toolbar ' + (toolbarOnFocus ? 'show-on-focus' : ''),
    toolbar: {
      options: ['inline', 'colorPicker'],
      inline: {options: ['bold', 'italic', 'underline']},
      fontSize: {
        options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
      },
      colorPicker: {
        component: CustomColorPicker,
      },
      link: {
        popupClassName: 'DraftEditor-basic-popup',
      },
    },
    toolbarOnFocus: toolbarOnFocus,
  };
  if (enableLink) {
    settings.toolbar.options.push('link');
  }
  if (enableTextAlign) {
    settings.toolbar.options.push('textAlign');
  }
  if (disableFontSize) {
    _remove(settings.toolbar.options, o => o === 'fontSize');
  }
  if (disableFontFamily) {
    _remove(settings.toolbar.options, o => o === 'fontFamily');
  }
  if (type === 'input') {
    settings.toolbarClassName = `${settings.toolbarClassName} input`;
    settings.editorClassName = `${settings.editorClassName} input`;
  } else {
    settings.toolbarClassName = `${settings.toolbarClassName} textarea`;
    settings.editorClassName = `${settings.editorClassName} textarea`;
  }
  if (fontList && fontList.length) {
    settings.toolbar.fontFamily = {
      options: fontList,
    };
  }
  const getTextLength = () => {
    const currentCnt = editorState.getCurrentContent();
    return currentCnt.getPlainText('').length;
  };

  const toHtmlWeExpect = htmlStr =>
    htmlStr
      .replace(/<p/, '<span')
      .replace('</p', '</span')
      .replace('<ins>', '<u>')
      .replace('</ins>', '</u>');
  const toEditorHtml = htmlStr =>
    htmlStr.replace('<u>', '<ins>').replace('</u>', '</ins>');

  const initText = () => {
    if (init && (field.value || value)) {
      const {contentBlocks, entityMap} = htmlToDraft(
        toEditorHtml(field.value || value)
      );
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );
      return EditorState.createWithContent(contentState);
    }
  };

  return (
    <div
      className={containerClassname ? containerClassname : 'col-xs-8'}
      style={containerStyle || {padding: 0}}
    >
      <Editor
        {...settings}
        readOnly={disabled}
        toolbarHidden={disabled || disableToolbar}
        editorState={init ? initText() : editorState}
        onEditorStateChange={e => {
          setEditorState(e);
          setInit(false);
        }}
        handleBeforeInput={val => {
          const textLength = editorState.getCurrentContent().getPlainText().length;
          if (val && textLength >= maxLength) {
            return 'handled';
          }
          return 'not-handled';
        }}
        handlePastedText={val => {
          const textLength = editorState.getCurrentContent().getPlainText().length;
          return ((val.length + textLength) >= maxLength);
        }}
        onChange={() => {
          const editorHtml = draftToHtml(
            convertToRaw(editorState.getCurrentContent())
          );
          const content = toHtmlWeExpect(editorHtml);
          form.setFieldValue(field.name || name, content);
          if (setTextLength) {
            setTextLength(getTextLength());
          }
        }}
        onBlur={() => {
          setInit(true);
          initText();
        }}
        {...editorProps}
        placeholder={placeholder}
      />
    </div>
  );
}
